import { forwardRef, useImperativeHandle, useState } from 'react';
import { apiJson } from '../api/client.js';
import { SearchableSelect } from './SearchableSelect.jsx';

export const FALLBACK_PROJECT_TYPES = [
  { key: 'site', name: 'Website' },
  { key: 'files', name: 'Files (Teams / SharePoint)' },
];

export function projectTypeLabel(types, key) {
  const match = (types || []).find((type) => type.key === key);
  if (match?.name) return match.name;
  return String(key || '').replace(/[_-]/g, ' ');
}

export function mergeTypeLists(current, incoming) {
  const byKey = new Map((incoming || []).map((type) => [type.key, type]));
  for (const type of current || []) {
    if (!byKey.has(type.key)) byKey.set(type.key, type);
  }
  return [...byKey.values()];
}

function mergeType(types, created) {
  if (types.some((type) => type.key === created.key)) {
    return types.map((type) => (type.key === created.key ? created : type));
  }
  return [...types, created].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.id - b.id);
}

/**
 * Shared project-type picker. A name that is not in the list is saved and
 * becomes available on every project and template form.
 */
export const ProjectTypeField = forwardRef(function ProjectTypeField(
  { value, onChange, types, onTypesChange },
  ref,
) {
  const [customOpen, setCustomOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');

  const known = types.length ? types : FALLBACK_PROJECT_TYPES;
  const options = known.some((type) => type.key === value) || !value
    ? known
    : [...known, { key: value, name: projectTypeLabel(known, value) }];

  const saveDraft = async (name) => {
    const trimmed = name.trim();
    if (!trimmed) {
      const message = 'Enter a type name.';
      setError(message);
      throw new Error(message);
    }
    setAdding(true);
    setError('');
    try {
      const created = await apiJson('/api/admin/project-types', {
        method: 'POST',
        body: { name: trimmed },
      });
      onTypesChange(mergeType(types.length ? types : FALLBACK_PROJECT_TYPES, created));
      onChange(created.key);
      setDraft('');
      setCustomOpen(false);
      return created.key;
    } catch (err) {
      setError(err.message || 'Could not add this type.');
      throw err;
    } finally {
      setAdding(false);
    }
  };

  useImperativeHandle(ref, () => ({
    resolve: () => (customOpen ? saveDraft(draft) : Promise.resolve(value)),
  }));

  return (
    <div className="stack" style={{ gap: '0.5rem' }}>
      <label className="field">
        <span>Type</span>
        <SearchableSelect
          value={customOpen ? '__custom__' : value}
          onChange={(ev) => {
            setError('');
            if (ev.target.value === '__custom__') {
              setCustomOpen(true);
              return;
            }
            setCustomOpen(false);
            setDraft('');
            onChange(ev.target.value);
          }}
        >
          {options.map((type) => (
            <option key={type.key} value={type.key}>
              {type.name}
            </option>
          ))}
          <option value="__custom__">Custom type…</option>
        </SearchableSelect>
      </label>
      {customOpen ? (
        <div className="stack" style={{ gap: '0.35rem' }}>
          <div className="row-actions" style={{ alignItems: 'flex-end' }}>
            <label className="field" style={{ flex: 1, margin: 0 }}>
              <span>New type</span>
              <input
                value={draft}
                maxLength={80}
                placeholder="Brand identity"
                disabled={adding}
                onChange={(ev) => {
                  setDraft(ev.target.value);
                  setError('');
                }}
                onKeyDown={(ev) => {
                  if (ev.key === 'Enter') {
                    ev.preventDefault();
                    void saveDraft(draft).catch(() => {});
                  }
                }}
              />
            </label>
            <button
              type="button"
              className="btn-secondary"
              disabled={adding || !draft.trim()}
              onClick={() => void saveDraft(draft).catch(() => {})}
            >
              {adding ? 'Adding…' : 'Add type'}
            </button>
          </div>
          <p className="muted small" style={{ margin: 0 }}>
            Saved types show up in this list for projects and templates.
          </p>
          {error ? <p className="err" style={{ margin: 0 }}>{error}</p> : null}
        </div>
      ) : null}
    </div>
  );
});
